import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { 
  collection, getDocs, query, orderBy, where, 
  doc, getDoc, Timestamp 
} from 'firebase/firestore';
import Sidebar from '../components/Sidebar';
import MobileHeader from '../components/MobileHeader';
import { showError, showSuccess } from '../utils/notifications';
import { exportReportsToExcel } from '../utils/excelExport';
import * as XLSX from 'xlsx';



// Helper function to get current date in Ecuador timezone
const getEcuadorDate = (daysOffset = 0) => {
  const now = new Date();
  const ecuadorOffset = -5 * 60; // Ecuador is UTC-5 (in minutes)
  const localOffset = now.getTimezoneOffset();
  const offsetDifference = (ecuadorOffset - localOffset) * 60 * 1000;
  
  const ecuadorDate = new Date(now.getTime() + offsetDifference);
  ecuadorDate.setDate(ecuadorDate.getDate() + daysOffset);
    return ecuadorDate.toISOString().split('T')[0];
};

function Reports() {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('reports');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    // Report data state
  const [reportData, setReportData] = useState({
    totalRecharges: 0,
    totalTickets: 0,
    totalRevenue: 0,
    rechargesByType: { moto: 0, auto: 0 },
    ticketsByType: { moto: 0, auto: 0 },
    rechargesByUser: [],
    dailyStats: []
  });  // Nuevo estado para las recargas detalladas
  const [allRecharges, setAllRecharges] = useState([]);
  const [loadingRecharges] = useState(false);
  
  // Estado para filtro de transferencias
  const [showOnlyTransfers, setShowOnlyTransfers] = useState(false);
  
  // Filter state - Initialize with Ecuador timezone
  const [dateFilter, setDateFilter] = useState({
    startDate: getEcuadorDate(0), // Today in Ecuador timezone
    endDate: getEcuadorDate(0),   // Today in Ecuador timezone
    showLastDays: 7,
    filterType: 'today' // 'today', 'custom', 'lastDays'
  });

  const [isFiltered, setIsFiltered] = useState(false);  const navigate = useNavigate();

  // Calculate report statistics
  const calculateReportStats = (recharges) => {
    const stats = {
      totalRecharges: recharges.length,
      totalTickets: 0,
      totalRevenue: 0,
      rechargesByType: { moto: 0, auto: 0 },
      ticketsByType: { moto: 0, auto: 0 },
      rechargesByUser: {},
      dailyStats: {}
    };

    recharges.forEach((recharge) => {
      // Count tickets and revenue
      if (recharge.operationType === 'recarga_tickets') {
        stats.totalTickets += recharge.ticketsAdded || 0;
        stats.totalRevenue += recharge.price || 0;
        
        // Count by vehicle type
        if (recharge.vehicleType === 'moto') {
          stats.rechargesByType.moto += recharge.price || 0;
          stats.ticketsByType.moto += recharge.ticketsAdded || 0;
        } else if (recharge.vehicleType === 'auto') {
          stats.rechargesByType.auto += recharge.price || 0;
          stats.ticketsByType.auto += recharge.ticketsAdded || 0;
        }
      } else if (recharge.operationType === 'recarga_balance') {
        stats.totalRevenue += recharge.amount || 0;
      }

      // Count by user
      const user = recharge.createdBy || 'Unknown';
      if (!stats.rechargesByUser[user]) {
        stats.rechargesByUser[user] = { count: 0, revenue: 0 };
      }
      stats.rechargesByUser[user].count += 1;
      stats.rechargesByUser[user].revenue += recharge.price || recharge.amount || 0;

      // Count by day
      const date = recharge.date?.seconds 
        ? new Date(recharge.date.seconds * 1000).toDateString()
        : new Date().toDateString();
      
      if (!stats.dailyStats[date]) {
        stats.dailyStats[date] = { count: 0, revenue: 0, tickets: 0 };
      }
      stats.dailyStats[date].count += 1;
      stats.dailyStats[date].revenue += recharge.price || recharge.amount || 0;
      if (recharge.operationType === 'recarga_tickets') {
        stats.dailyStats[date].tickets += recharge.ticketsAdded || 0;
      }
    });

    // Convert objects to arrays for easier display
    stats.rechargesByUser = Object.entries(stats.rechargesByUser)
      .map(([user, data]) => ({ user, ...data }))
      .sort((a, b) => b.revenue - a.revenue);

    stats.dailyStats = Object.entries(stats.dailyStats)
      .map(([date, data]) => ({ date, ...data }))
      .sort((a, b) => new Date(b.date) - new Date(a.date)); // Show all days in the selected range

    setReportData(stats);
  };

  // Load report data based on user role
  const loadReportData = useCallback(async (userRole) => {
    try {
      // Filtros de fecha
      let startTimestamp = null;
      let endTimestamp = null;

      if (dateFilter.startDate) {
        const parts = dateFilter.startDate.split('-');
        const d = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
        d.setHours(0, 0, 0, 0);
        startTimestamp = Timestamp.fromDate(d);
      }
      if (dateFilter.endDate) {
        const parts = dateFilter.endDate.split('-');
        const d = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
        d.setHours(23, 59, 59, 999);
        endTimestamp = Timestamp.fromDate(d);
      }

      // Construir constraints comunes
      const buildConstraints = () => {
        const c = [orderBy('date', 'desc')];
        if (startTimestamp) c.push(where('date', '>=', startTimestamp));
        if (endTimestamp)   c.push(where('date', '<=', endTimestamp));
        if (userRole === 'Supervisor') c.push(where('createdBy', '==', auth.currentUser.email));
        return c;
      };

      // Consultar ambas colecciones en paralelo
      const [snapInternas, snapRecharge] = await Promise.all([
        getDocs(query(collection(db, 'RechargeInternas'), ...buildConstraints())),
        getDocs(query(collection(db, 'Recharge'),         ...buildConstraints())),
      ]);

      // Combinar y deduplicar por fingerprint
      const fingerprint = (data) =>
        `${data.fechaDocumento}|${data.numberCa}|${data.vehicleType}|${data.ticketsAdded}|${data.tokenticked}|${data.vendorId}`;

      const huellasVistas = new Set();
      const recharges = [];

      const agregarSiNoEsDuplicado = (source) => (doc) => {
        const data = doc.data();
        const fp = fingerprint(data);
        if (!huellasVistas.has(fp)) {
          huellasVistas.add(fp);
          recharges.push({ id: doc.id, source, ...data });
        }
      };

      snapInternas.forEach(agregarSiNoEsDuplicado('RI'));
      snapRecharge.forEach(agregarSiNoEsDuplicado('SD'));

      // Ordenar por fecha desc
      recharges.sort((a, b) => (b.date?.seconds ?? 0) - (a.date?.seconds ?? 0));

      console.log(`✅ RechargeInternas: ${snapInternas.size} | Recharge: ${snapRecharge.size} | Combinados (sin duplicados): ${recharges.length}`);

      // Calculate statistics
      calculateReportStats(recharges);
      
      // Guardar todas las recargas para la tabla detallada
      setAllRecharges(recharges);

      // Check if we're using filters
      const todayEcuador = getEcuadorDate(0);
      setIsFiltered(
        dateFilter.filterType !== 'today' || 
        dateFilter.startDate !== todayEcuador || 
        dateFilter.endDate !== todayEcuador
      );
      
    } catch (error) {
      console.error('Error loading report data:', error);
      showError('Error', 'No se pudieron cargar los datos del reporte');
    }
  }, [dateFilter]);

  // Auth and user verification
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      try {
        if (currentUser) {
          const userDocRef = doc(db, 'usuarios', currentUser.email);
          const userDoc = await getDoc(userDocRef);
          
          if (!userDoc.exists()) {
            showError("Usuario no encontrado", "Tu perfil no existe en el sistema");
            await auth.signOut();
            navigate('/login');
            return;
          }
          
          const userData = userDoc.data();
          setUser(currentUser);
          setUserRole(userData.rol || 'Usuario');
            // Load report data after authentication
          await loadReportData(userData.rol);
        } else {
          navigate('/login');
          return;
        }
        setLoading(false);
      } catch (error) {
        console.error("Error al verificar usuario:", error);
        showError("Error", "Ocurrió un problema al verificar tu perfil");
        await auth.signOut();
        navigate('/login');
      }    });

    return () => unsubscribe();
  }, [navigate, loadReportData]);

  // Load report data when filters change
  useEffect(() => {
    if (user && userRole) {
      loadReportData(userRole);
    }  }, [dateFilter, user, userRole, loadReportData]);

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    switch (filterType) {case 'today':
        const todayStr = getEcuadorDate(0);
        console.log('📅 Filtro HOY activado (Ecuador):', todayStr);
        
        setDateFilter({
          startDate: todayStr,
          endDate: todayStr,
          showLastDays: 1,
          filterType: 'today'
        });
        break;
      case 'custom':
        console.log('⚙️ Filtro PERSONALIZADO activado');
        setDateFilter(prev => ({
          ...prev,
          filterType: 'custom'
        }));
        break;      case 'startDate':
        console.log('📅 Cambiando fecha inicio a:', value);
        console.log('📅 Valor recibido del input:', {
          valor: value,
          tipo: typeof value,
          formatoEsperado: 'YYYY-MM-DD'
        });
        
        // Validate that the date range doesn't exceed 90 days
        const newStartDate = new Date(value);
        const currentEndDate = new Date(dateFilter.endDate);
        const daysDifference = Math.abs((currentEndDate - newStartDate) / (1000 * 60 * 60 * 24));
        
        if (daysDifference > 90) {
          showError('Rango de Fechas', 'El rango de fechas no puede ser mayor a 90 días');
          return;
        }
          console.log('✅ Fecha inicio válida, aplicando:', value);
        setDateFilter(prev => ({
          ...prev,
          startDate: value,
          filterType: 'custom'
        }));
        break;      case 'endDate':
        console.log('📅 Cambiando fecha fin a:', value);
        console.log('📅 Valor recibido del input:', {
          valor: value,
          tipo: typeof value,
          formatoEsperado: 'YYYY-MM-DD'
        });
        
        // Validate that the date range doesn't exceed 90 days
        const currentStartDate = new Date(dateFilter.startDate);
        const newEndDate = new Date(value);
        const daysDiff = Math.abs((newEndDate - currentStartDate) / (1000 * 60 * 60 * 24));
        
        if (daysDiff > 90) {
          showError('Rango de Fechas', 'El rango de fechas no puede ser mayor a 90 días');
          return;
        }
        
        console.log('✅ Fecha fin válida, aplicando:', value);
        setDateFilter(prev => ({
          ...prev,
          endDate: value,
          filterType: 'custom'
        }));
        break;
      default:
        break;
    }
  };
  // Clear filters and go back to today
  const clearFilters = () => {
    handleFilterChange('today');
  };

  // Handle Excel export
  const handleExcelExport = () => {
    try {
      const result = exportReportsToExcel(reportData, dateFilter, userRole);
      
      if (result.success) {
        showSuccess(
          'Descarga Exitosa', 
          `El archivo ${result.filename} ha sido descargado correctamente`
        );
      } else {
        showError('Error de Descarga', result.message);
      }
    } catch (error) {
      console.error('Excel export error:', error);
      showError('Error', 'No se pudo generar el archivo Excel');
    }
  };

  // Nueva función para exportar todas las recargas a Excel
  const exportAllRechargesToExcel = () => {
    try {
      if (allRecharges.length === 0) {
        showError("Advertencia", "No hay datos para exportar");
        return;
      }

      // Preparar datos para Excel
      const excelData = allRecharges.map(recharge => ({
        userName: recharge.userName || (recharge.createdBy ? (recharge.createdBy.split ? recharge.createdBy.split('@')[0] : recharge.createdBy) : ''),
        numberCa: recharge.numberCa || recharge.numberCA || recharge.numeroCA || recharge.caNumber || '',
        'ID': recharge.id || '',
        'Fecha': recharge.date?.seconds 
          ? new Date(recharge.date.seconds * 1000).toLocaleString('es-ES')
          : new Date().toLocaleString('es-ES'),
        'Tipo Operación': recharge.operationType || '',
        'Tipo de Pago': recharge.paymentMethod || recharge.metodoPago || 'Efectivo',
        'Tipo Vehículo': recharge.vehicleType || '',
        'Precio': recharge.price || 0,
        'Cantidad': recharge.amount || 0,
        'Tickets Agregados': recharge.ticketsAdded || 0,
        'Origen': recharge.source || '',
        'Creado Por': recharge.createdBy || '',
        'Descripción': recharge.description || '',
        'Token': recharge.tokenticked || recharge.tokenTicked || recharge.token || 'N/A'
      }));

      // Crear el libro de trabajo
      const workbook = XLSX.utils.book_new();
      
      // Crear la hoja de trabajo
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      
      // Agregar la hoja al libro
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Recargas');
      
      // Generar nombre del archivo
      const dateRange = dateFilter.startDate === dateFilter.endDate 
        ? dateFilter.startDate 
        : `${dateFilter.startDate}_${dateFilter.endDate}`;
      const timestamp = new Date().toISOString().slice(0, 16).replace(/[:-]/g, '').replace('T', '_');
      const filename = `Todas_Recargas_${dateRange}_${timestamp}.xlsx`;
      
      // Descargar el archivo
      XLSX.writeFile(workbook, filename);
      
      showSuccess("Éxito", `Archivo ${filename} descargado correctamente`);
      
    } catch (error) {
      console.error('Error exportando a Excel:', error);
      showError("Error", "No se pudo exportar el archivo Excel");
    }
  };

  // Función para formatear moneda
  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      {/* Mobile Header */}
      <MobileHeader 
        title="Reportes" 
        userRole={userRole}
        toggleMenu={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      />
      
      {/* Add top spacing for mobile */}
      <div className="md:hidden h-14"></div>

      <div className="flex flex-1 overflow-hidden">
        {/* Overlay for mobile menu */}
        {isMobileMenuOpen && (
          <div 
            className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-10"
            onClick={() => setIsMobileMenuOpen(false)}
          ></div>
        )}
      
        {/* Sidebar */}
        <Sidebar 
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
        />

        {/* Main Content */}
        <div className="flex-1 overflow-hidden">
          {/* Header */}
          <header className="bg-white shadow border-b border-gray-200">
            <div className="px-6 py-4">
              <div className="flex items-center justify-between">                <div>
                  <h1 className="text-2xl font-bold text-gray-900">📊 Reportes</h1>
                  <p className="text-sm text-gray-600 mt-1 hidden md:block">
                    Análisis y estadísticas del sistema
                  </p>
                </div>
                <div className="flex items-center space-x-4">
                  {/* Excel Export Button - Hidden on mobile */}
                  <button
                    onClick={handleExcelExport}
                    disabled={reportData.totalRecharges === 0}
                    className={`hidden md:flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      reportData.totalRecharges === 0
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-green-600 hover:bg-green-700 text-white shadow-md hover:shadow-lg transform hover:scale-105'
                    }`}
                    title={reportData.totalRecharges === 0 ? 'No hay datos para exportar' : 'Descargar reporte en Excel'}
                  >
                    <span className="mr-2">📊</span>
                    Descargar Excel
                  </button>
                  
                  {/* User Info - Simplified on mobile */}
                  <div className="text-sm text-gray-600">
                    <span className="hidden md:inline">Usuario: </span>
                    <span className="font-medium hidden md:inline">{user?.email}</span> 
                    <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                      {userRole}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </header>          {/* Content */}
          <main className="p-6 overflow-y-auto">
            {/* Date Filters */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-700">📅 Filtros de Fecha</h3>
                {isFiltered && (
                  <button
                    onClick={clearFilters}
                    className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded text-sm"
                  >
                    Limpiar Filtros
                  </button>
                )}
              </div>              {/* Quick Filter Buttons */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                <button
                  onClick={() => handleFilterChange('today')}
                  className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                    dateFilter.filterType === 'today'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Hoy
                </button>
                <button
                  onClick={() => handleFilterChange('custom')}
                  className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                    dateFilter.filterType === 'custom'
                      ? 'bg-purple-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Personalizado
                </button>
              </div>

              {/* Custom Date Range */}
              {dateFilter.filterType === 'custom' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 p-4 bg-gray-50 rounded">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fecha Inicio
                    </label>
                    <input
                      type="date"
                      value={dateFilter.startDate}
                      onChange={(e) => handleFilterChange('startDate', e.target.value)}
                      className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fecha Fin
                    </label>
                    <input
                      type="date"
                      value={dateFilter.endDate}
                      onChange={(e) => handleFilterChange('endDate', e.target.value)}
                      className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              )}
            </div>            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Recargas</p>
                    <p className="text-2xl font-bold text-blue-600">{reportData.totalRecharges}</p>
                  </div>
                  <div className="text-3xl">📈</div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Tickets</p>
                    <p className="text-2xl font-bold text-green-600">{reportData.totalTickets}</p>
                    <div className="text-xs text-gray-700 mt-1">Motos: <span className="font-bold">{reportData.ticketsByType?.moto || 0}</span></div>
                    <div className="text-xs text-gray-700">Autos: <span className="font-bold">{reportData.ticketsByType?.auto || 0}</span></div>
                  </div>
                  <div className="text-3xl">🎫</div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6 border-l-4 border-purple-500">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Ingresos Totales</p>
                    <p className="text-2xl font-bold text-purple-600">${reportData.totalRevenue.toFixed(2)}</p>
                  </div>
                  <div className="text-3xl">💰</div>                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {/* Vehicle Type Distribution */}
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-800">Distribución por Tipo de Vehículo</h2>
                </div>
                <div className="p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="text-2xl mr-3">🏍️</div>
                      <span className="text-gray-700">Motos</span>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-blue-600">${reportData.rechargesByType.moto.toFixed(2)}</div>
                      <div className="text-sm text-gray-500">
                        {reportData.totalRevenue > 0 ? ((reportData.rechargesByType.moto / reportData.totalRevenue) * 100).toFixed(1) : 0}%
                      </div>
                      <div className="text-xs text-gray-700 mt-1">Tickets: <span className="font-bold">{reportData.ticketsByType?.moto || 0}</span></div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="text-2xl mr-3">🚗</div>
                      <span className="text-gray-700">Autos</span>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-green-600">${reportData.rechargesByType.auto.toFixed(2)}</div>
                      <div className="text-sm text-gray-500">
                        {reportData.totalRevenue > 0 ? ((reportData.rechargesByType.auto / reportData.totalRevenue) * 100).toFixed(1) : 0}%
                      </div>
                      <div className="text-xs text-gray-700 mt-1">Tickets: <span className="font-bold">{reportData.ticketsByType?.auto || 0}</span></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Top Users */}
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-800">Top Usuarios por Ingresos</h2>
                </div>
                <div className="p-6">
                  <div className="space-y-3">
                    {reportData.rechargesByUser.slice(0, 5).map((userStat, index) => (
                      <div key={userStat.user} className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold mr-3">
                            {index + 1}
                          </div>
                          <span className="text-gray-700 truncate max-w-40" title={userStat.user}>
                            {userStat.user.split('@')[0]}
                          </span>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-semibold text-gray-900">${userStat.revenue.toFixed(2)}</div>
                          <div className="text-xs text-gray-500">{userStat.count} recargas</div>
                        </div>
                      </div>
                    ))}
                    {reportData.rechargesByUser.length === 0 && (
                      <p className="text-gray-500 text-center py-4">No hay datos disponibles</p>
                    )}
                  </div>
                </div>
              </div>
            </div>            {/* Daily Statistics */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-semibold text-gray-800">
                    📈 Estadísticas por Día
                    {dateFilter.filterType === 'today' && ' - Hoy'}
                    {dateFilter.filterType === 'lastDays' && ` - Últimos ${dateFilter.showLastDays} días`}
                    {dateFilter.filterType === 'all' && ' - Todos los Datos Históricos'}
                    {dateFilter.filterType === 'custom' && ' - Rango Personalizado'}
                  </h2>
                  <div className="flex items-center space-x-3">
                    <span className="text-sm text-gray-500">
                      {reportData.dailyStats.length} día(s) con datos
                    </span>
                    {reportData.dailyStats.length > 0 && (
                      <button
                        onClick={handleExcelExport}
                        className="flex items-center px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm rounded transition-colors"
                        title="Descargar datos en Excel"
                      >
                        <span className="mr-1">📥</span>
                        Excel
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Fecha
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Recargas
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tickets
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ingresos
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {reportData.dailyStats.map((dayStat, index) => {
                      const isToday = new Date(dayStat.date).toDateString() === new Date().toDateString();
                      return (
                        <tr key={index} className={`hover:bg-gray-50 ${isToday ? 'bg-blue-50' : ''}`}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div className="flex items-center">
                              {isToday && <span className="text-blue-500 mr-2">🔵</span>}
                              {new Date(dayStat.date).toLocaleDateString('es-ES', {
                                weekday: 'short',
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric'
                              })}
                              {isToday && <span className="ml-2 text-xs text-blue-600 font-medium">(Hoy)</span>}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                            <span className="font-medium">{dayStat.count}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                            <span className="font-medium text-purple-600">{dayStat.tickets}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                            ${dayStat.revenue.toFixed(2)}
                          </td>
                        </tr>
                      );
                    })}
                    {reportData.dailyStats.length === 0 && (
                      <tr>
                        <td colSpan="4" className="px-6 py-10 text-center text-gray-500">
                          <div className="space-y-2">
                            <div className="text-4xl">📭</div>
                            <p>No hay datos disponibles para el período seleccionado</p>
                            <p className="text-xs">
                              {dateFilter.filterType === 'today' && 'No se encontraron recargas para hoy'}
                              {dateFilter.filterType === 'lastDays' && `No se encontraron recargas en los últimos ${dateFilter.showLastDays} días`}
                              {dateFilter.filterType === 'all' && 'No hay datos históricos disponibles'}
                              {dateFilter.filterType === 'custom' && 'No se encontraron recargas en el rango seleccionado'}
                            </p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Nueva sección: Todas las Recargas */}
            <div className="bg-white rounded-lg shadow mt-8">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-semibold text-gray-800">
                    📋 Todas las Recargas de la Colección RechargeInternas
                    {dateFilter.filterType === 'today' && ' - Hoy'}
                    {dateFilter.filterType === 'custom' && ' - Rango Personalizado'}
                  </h2>
                  <div className="flex items-center space-x-3">
                    <span className="text-sm text-gray-500">
                      {showOnlyTransfers 
                        ? allRecharges.filter(r => (r.paymentMethod || r.metodoPago) === 'transferencia').length 
                        : allRecharges.length
                      } recarga(s) encontrada(s)
                    </span>
                    {allRecharges.length > 0 && (
                      <>
                        <button
                          onClick={() => setShowOnlyTransfers(!showOnlyTransfers)}
                          className={`flex items-center px-3 py-1 text-white text-sm rounded transition-colors ${
                            showOnlyTransfers 
                              ? 'bg-purple-600 hover:bg-purple-700' 
                              : 'bg-gray-600 hover:bg-gray-700'
                          }`}
                          title={showOnlyTransfers ? "Mostrar todas las recargas" : "Ver solo transferencias"}
                        >
                          <span className="mr-1">🔍</span>
                          {showOnlyTransfers ? 'Ver Todas' : 'Ver Transferencias'}
                        </button>
                        <button
                          onClick={exportAllRechargesToExcel}
                          className="flex items-center px-3 py-1 bg-orange-600 hover:bg-orange-700 text-white text-sm rounded transition-colors"
                          title="Descargar todas las recargas en Excel"
                        >
                          <span className="mr-1">📊</span>
                          Descargar Excel
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
              
              {loadingRecharges ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                  <span className="ml-2 text-gray-600">Cargando recargas...</span>
                </div>
              ) : allRecharges.length === 0 ? (
                <div className="text-center py-12">
                  <div className="space-y-2">
                    <div className="text-4xl">📭</div>
                    <p className="text-gray-500">No se encontraron recargas con los filtros aplicados</p>
                    <p className="text-xs text-gray-400">
                      {dateFilter.filterType === 'today' && 'No hay recargas para hoy'}
                      {dateFilter.filterType === 'custom' && 'No hay recargas en el rango seleccionado'}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Fecha
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Tipo Operación
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Tipo de Pago
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Vehículo
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Precio
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Cantidad
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Tickets
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Origen
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Creado Por
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Token
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {(showOnlyTransfers 
                        ? allRecharges.filter(r => (r.paymentMethod || r.metodoPago) === 'transferencia')
                        : allRecharges
                      ).map((recharge, index) => (
                        <tr key={recharge.id || index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {recharge.date?.seconds 
                              ? new Date(recharge.date.seconds * 1000).toLocaleString('es-ES')
                              : 'N/A'
                            }
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-600">
                            {recharge.id ? recharge.id.substring(0, 8) + '...' : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              recharge.operationType === 'recarga_tickets' 
                                ? 'bg-blue-100 text-blue-800' 
                                : recharge.operationType === 'recarga_balance'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {recharge.operationType || 'N/A'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              (recharge.paymentMethod || recharge.metodoPago) === 'transferencia'
                                ? 'bg-purple-100 text-purple-800' 
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {(recharge.paymentMethod || recharge.metodoPago || 'Efectivo')}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              recharge.vehicleType === 'moto' 
                                ? 'bg-orange-100 text-orange-800' 
                                : recharge.vehicleType === 'auto'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {recharge.vehicleType || 'N/A'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                            {formatCurrency(recharge.price)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {recharge.amount || 0}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {recharge.ticketsAdded || 0}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {recharge.source === 'SD' ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-orange-100 text-orange-700">SD</span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-indigo-100 text-indigo-700">RI</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div className="text-sm font-medium text-gray-900">
                              {recharge.createdBy ? recharge.createdBy.split('@')[0] : 'N/A'}
                            </div>
                            {recharge.createdBy && (
                              <div className="text-xs text-gray-500">
                                {recharge.createdBy}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-mono font-medium bg-indigo-100 text-indigo-800">
                              {recharge.tokenticked || recharge.tokenTicked || recharge.token || 'N/A'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

export default Reports;
